// src/services/ipdPrescriptionService.js

import apiClient from "./apiClient";
import { linkPrescriptionToAdmission } from "./PatientAdmissionService";

const API_BASE_URL = `/ipd-prescriptions`; // Use relative path since apiClient has baseURL

const toPositiveRelationId = (value) => {
  if (value == null || value === "") return null;
  if (typeof value === "object") {
    if (Array.isArray(value)) return null;
    return toPositiveRelationId(
      value.id ??
        value.admissionId ??
        value.admission_id ??
        value.prescriptionId ??
        value.prescription_id
    );
  }
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.trunc(n);
};

const resolveIpdPrescriptionRecordId = (record) => {
  if (record == null || record === "") return null;
  if (typeof record !== "object") return toPositiveRelationId(record);
  return toPositiveRelationId(
    record.id ??
      record.prescriptionId ??
      record.prescription_id ??
      record.data?.id ??
      record.data?.prescriptionId ??
      record.data?.prescription_id
  );
};

/**
 * Send admissionId as both camelCase and snake_case so org IPD prescription
 * inserts persist admission_id regardless of Jackson naming.
 * Omit the field when missing so unrelated payloads stay unchanged.
 */
export const normalizeIpdPrescriptionRelationIds = (prescription = {}) => {
  if (!prescription || typeof prescription !== "object" || Array.isArray(prescription)) {
    return prescription;
  }

  const payload = { ...prescription };
  const admissionId = toPositiveRelationId(
    payload.admissionId ?? payload.admission_id ?? payload.admission
  );

  if (admissionId != null) {
    payload.admissionId = admissionId;
    payload.admission_id = admissionId;
  } else {
    delete payload.admissionId;
    delete payload.admission_id;
  }

  return payload;
};

const linkIpdPrescriptionAdmissionIfPossible = async (
  requestPayload,
  createdPrescription,
  admissionSnapshot
) => {
  const admissionId = toPositiveRelationId(
    requestPayload?.admissionId ?? requestPayload?.admission_id
  );
  const prescriptionId = resolveIpdPrescriptionRecordId(createdPrescription);
  if (!admissionId || !prescriptionId) return;

  try {
    await linkPrescriptionToAdmission(
      admissionId,
      prescriptionId,
      admissionSnapshot
    );
  } catch (linkError) {
    // Prescription already saved — do not fail doctor submit for the reverse link.
    console.warn(
      "[ipdPrescriptionService] IPD prescription created but admission.prescription_id was not updated:",
      linkError
    );
  }
};

export const createIpdPrescription = async (prescriptionData, options = {}) => {
  try {
    const { organizationId: orgIdInBody, ...prescriptionWithoutOrg } =
      prescriptionData || {};
    const payload = normalizeIpdPrescriptionRelationIds(prescriptionWithoutOrg);
    // apiClient will automatically add organizationId via interceptor
    const response = await apiClient.post(`${API_BASE_URL}/create`, payload);
    await linkIpdPrescriptionAdmissionIfPossible(
      payload,
      response.data,
      options?.admission || null
    );
    return response.data;
  } catch (error) {
    console.error("Error creating IPD prescription:", error);
    throw error;
  }
};

export const getIpdPrescriptionsByPatientId = async (patientId) => {
  try {
    // apiClient will automatically add organizationId as query param via interceptor
    const response = await apiClient.get(`${API_BASE_URL}/patient/${patientId}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching IPD prescriptions by patient:", error);
    throw error;
  }
};

export const getIpdPrescriptionsByAdmissionId = async (admissionId) => {
  try {
    // apiClient will automatically add organizationId as query param via interceptor
    const response = await apiClient.get(`${API_BASE_URL}/admission/${admissionId}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching IPD prescriptions by admission:", error);
    throw error;
  }
};

export const getIpdPrescriptionsByDoctorId = async (doctorId) => {
  try {
    // apiClient will automatically add organizationId as query param via interceptor
    const response = await apiClient.get(`${API_BASE_URL}/doctor/${doctorId}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching IPD prescriptions by doctor:", error);
    throw error;
  }
};

export const updateIpdPrescription = async (id, prescriptionData) => {
  try {
    // apiClient will automatically add organizationId via interceptor
    const response = await apiClient.put(
      `${API_BASE_URL}/${id}`,
      normalizeIpdPrescriptionRelationIds(prescriptionData)
    );
    return response.data;
  } catch (error) {
    console.error("Error updating IPD prescription:", error);
    throw error;
  }
};

export const deleteIpdPrescription = async (id) => {
  try {
    // apiClient will automatically add organizationId as query param via interceptor
    await apiClient.delete(`${API_BASE_URL}/${id}`);
  } catch (error) {
    console.error("Error deleting IPD prescription:", error);
    throw error;
  }
};

// Integration with existing IPD treatments
export const convertPrescriptionToTreatments = async (prescriptionId) => {
  try {
    // apiClient will automatically add organizationId via interceptor
    const response = await apiClient.post(`${API_BASE_URL}/${prescriptionId}/convert-to-treatments`);
    return response.data;
  } catch (error) {
    console.error("Error converting prescription to treatments:", error);
    throw error;
  }
};

// Get prescription with treatment details
export const getIpdPrescriptionWithTreatments = async (prescriptionId) => {
  try {
    // apiClient will automatically add organizationId as query param via interceptor
    const response = await apiClient.get(`${API_BASE_URL}/${prescriptionId}/with-treatments`);
    return response.data;
  } catch (error) {
    console.error("Error fetching prescription with treatments:", error);
    throw error;
  }
};
